/**
 * Custom Card Grid Template
 */

import { escapeHtml, createBlockWrapper, handleImageError } from '../utils';
import { applyThemeOverrides, extractItems, getNestedValue } from './utils';
import type { CardFieldConfig, TemplateRenderer } from './types';

export const renderCustomCardGrid: TemplateRenderer = (
  config,
  data,
  container,
  onAction,
  themeOverrides
) => {
  const columns = (config.columns as number) || 3;
  const cardFields = (config.cardFields as CardFieldConfig[]) || [];
  const actionLabel = (config.actionLabel as string) || 'View';

  // Data should be an array of items or have a 'cards' or 'items' property
  const items = extractItems(data);

  const wrapper = createBlockWrapper('custom-card-grid');
  applyThemeOverrides(wrapper, themeOverrides);

  const grid = document.createElement('div');
  grid.className = 'pms-cards-grid';
  grid.style.setProperty('--pms-card-columns', String(columns));

  for (const item of items) {
    const cardEl = document.createElement('div');
    cardEl.className = 'pms-card pms-custom-card';

    let html = '';

    // Render fields according to config
    for (const field of cardFields) {
      const value = getNestedValue(item, field.key);
      if (value === undefined || value === null) continue;

      switch (field.type) {
        case 'image':
          html += `<img class="pms-card-image" src="${escapeHtml(String(value))}" alt="" loading="lazy" />`;
          break;
        case 'title':
          html += `<div class="pms-card-title">${escapeHtml(String(value))}</div>`;
          break;
        case 'text':
          html += `<div class="pms-card-description">${escapeHtml(String(value))}</div>`;
          break;
        case 'highlight':
          const prefix = field.prefix || '';
          const suffix = field.suffix || '';
          html += `<div class="pms-card-price">${escapeHtml(prefix)}${escapeHtml(String(value))}${escapeHtml(suffix)}</div>`;
          break;
        case 'badge':
          html += `<div class="pms-card-badge">${escapeHtml(String(value))}</div>`;
          break;
      }
    }

    // Action button
    if (actionLabel) {
      const actionValue = (item as Record<string, unknown>).id || (item as Record<string, unknown>).url || '';
      html += `<div class="pms-card-actions">
        <button class="pms-card-action" data-action="${escapeHtml(String(actionValue))}">${escapeHtml(actionLabel)}</button>
      </div>`;
    }

    cardEl.innerHTML = html;

    // Handle image errors
    const img = cardEl.querySelector('.pms-card-image') as HTMLImageElement;
    if (img) handleImageError(img);

    // Bind action
    const actionBtn = cardEl.querySelector('.pms-card-action');
    if (actionBtn && onAction) {
      actionBtn.addEventListener('click', () => {
        const action = (actionBtn as HTMLElement).dataset.action;
        if (action) onAction(`custom:${action}`);
      });
    }

    grid.appendChild(cardEl);
  }

  wrapper.appendChild(grid);
  container.appendChild(wrapper);
};
